import type { JSX } from 'react'

import { TextBox, TextBoxTypes } from 'atoms/TextBox'
import { Colors, TextAligning } from 'atoms/TextBox/TextBox.h'
import { useCountdown } from 'utils/hooks'
import {
  CountdownDigitSC,
  CountdownDigitsSC,
  CountdownSeparatorSC,
  CountdownTimerSC,
  CountdownUnitSC,
} from './CountdownTimer.styled'

export interface ICountdownTimerProps {
  /** Date the countdown runs down to, as accepted by `new Date()`. */
  completionDate: string
  daysTitle?: string
  hoursTitle?: string
  minutesTitle?: string
  /** Fires once the countdown reaches `completionDate`. */
  onTimerCompleted?: () => void
  className?: string
}

/** Splits a unit into the two-digit tiles the concept draws it with. */
const digitsOf = (value: number): string[] =>
  String(Math.max(value, 0)).padStart(2, '0').split('')

const CountdownUnit = ({
  value,
  title,
}: {
  value: number
  title?: string
}): JSX.Element => (
  <CountdownUnitSC>
    <CountdownDigitsSC>
      {digitsOf(value).map((digit, index) => (
        <CountdownDigitSC key={index}>
          <TextBox
            title={digit}
            font={TextBoxTypes.Font4xlBold}
            color={Colors.TEXT_ICON_PRIMARY}
            alignment={TextAligning.Center}
            maxLines={1}
          />
        </CountdownDigitSC>
      ))}
    </CountdownDigitsSC>
    {title && (
      <TextBox
        title={title}
        font={TextBoxTypes.FontSm}
        color={Colors.TEXT_ICON_SECONDARY}
        alignment={TextAligning.Center}
        maxLines={1}
      />
    )}
  </CountdownUnitSC>
)

const CountdownSeparator = (): JSX.Element => (
  <CountdownSeparatorSC>
    <TextBox
      title=':'
      font={TextBoxTypes.Font4xlBold}
      color={Colors.TEXT_ICON_PRIMARY}
      alignment={TextAligning.Center}
      maxLines={1}
    />
  </CountdownSeparatorSC>
)

/**
 * `countdown timer` from the content details concept: days, hours and minutes
 * left before an event starts, each digit on its own tile.
 */
export const CountdownTimer = ({
  completionDate,
  daysTitle,
  hoursTitle,
  minutesTitle,
  onTimerCompleted,
  className,
}: ICountdownTimerProps): JSX.Element | null => {
  const { days, hours, minutes, seconds } = useCountdown(
    completionDate,
    onTimerCompleted
  )

  if (days < 0 || hours < 0 || minutes < 0 || seconds < 0) return null

  const dayDigits = Math.max(2, String(Math.max(days, 0)).length)

  return (
    <CountdownTimerSC
      className={className}
      data-testid='countdown-timer'
      data-day-digits={dayDigits >= 3 ? 3 : 2}
    >
      <CountdownUnit value={days} title={daysTitle} />
      <CountdownSeparator />
      <CountdownUnit value={hours} title={hoursTitle} />
      <CountdownSeparator />
      <CountdownUnit value={minutes} title={minutesTitle} />
    </CountdownTimerSC>
  )
}
